import { useEffect } from 'react';
import {
  CapacityManagementRootResponse,
  ShipperTransactionsParams,
  TransactionsResponse,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';
import urlTemplate from 'url-template';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: ShipperTransactionsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: ShipperTransactionsParams;
};

export function useShipperTransactions({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const shipperTransactions = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      const link =
        rootData._links?.myTsoTransactionsV2 ??
        rootData._links?.myTsoTransactionsV3;
      if (!link) throw new Forbidden();

      const url = urlTemplate.parse(link.href).expand({});

      return axios.request<TransactionsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        // as this is a non-lazy request we want to handle the error only,
        // if we have at least one successful response
        if (isConstraintViolation(error) && shipperTransactions.response) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    shipperTransactions.execute(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return shipperTransactions;
}

export type ShipperTransactionsRequest = ReturnType<
  typeof useShipperTransactions
>;
